import type { AllowanceLine } from '../award-rate-lookup';
import type {
  CompiledConditionalPenaltyLine,
  CompiledEbaOverlay,
  ManualBlockContract,
  StructuralBlock,
} from './eba-block-compiler';
import {
  hhmmToMinutes,
  timeInWindow,
  hasGeneralStaffDailyLoadedOvertime,
} from './eba-conditional-penalties';
import {
  ebaCertReducerBandUncoveredReason,
  isEbaCertReducerBandInScope,
  type CertDayType,
  type CertEmployment,
  type CertShiftBand,
  type CertShiftInput,
} from './eba-cert-reducer';

// 1.0.5 — generate category-scoped shift-loading scenarios (matching shift +
// employee_category per windowMode) so the cert exercises the cl 44 / cl 6 path.
// 1.0.6 — exercise combined loading + OT for the persisted GS additive daily pair.
export const EBA_CERT_MATRIX_GENERATOR_VERSION = '1.0.7';

export interface CertScenario {
  id: string;
  label: string;
  input: CertShiftInput;
  exercisesBands: string[];
  exercisesAllowances: string[];
  exercisesConditionalPenalties?: string[];
}

export interface CertCoverageReport {
  automatedBands: string[];
  coveredBands: string[];
  uncoveredBands: Array<{ band: string; reason: string }>;
  manualContracts: ManualBlockContract[];
  structuralUnsupported: StructuralBlock[];
  automatedAllowances: string[];
  coveredAllowances: string[];
  uncoveredAllowances: Array<{ allowanceCode: string; reason: string }>;
  automatedConditionalPenaltyCodes: string[];
  coveredConditionalPenaltyCodes: string[];
  uncoveredConditionalPenalties: Array<{ blockCode: string; reason: string }>;
}

export interface GenerateCertMatrixOptions {
  baseAwardCode: string;
  sampleRatesByClassification: Record<string, number>;
  ordinaryHoursLimit?: number;
  ordinaryHoursWorked?: number;
  overtimeCrossingHours?: number;
  allowanceUnitsByCode?: Record<string, number>;
}

const EMPLOYMENTS: CertEmployment[] = ['PERMANENT', 'CASUAL'];
const BAND_ORDER = [
  'WEEKDAY_DAY',
  'SATURDAY',
  'SUNDAY',
  'PUBLIC_HOLIDAY',
  'WEEKDAY_AFTERNOON',
  'WEEKDAY_NIGHT',
  'PERMANENT_NIGHT_SHIFT',
  'OVERTIME_FIRST_2H',
  'OVERTIME_AFTER_2H',
];

function defaultOrdinaryHoursLimit(baseAwardCode: string): number {
  switch (baseAwardCode) {
    case 'MA000018':
      return 10;
    case 'MA000034':
      return 8;
    default:
      return 8;
  }
}

function ordinaryHoursLimitFor(opts: GenerateCertMatrixOptions): number {
  return opts.ordinaryHoursLimit ?? defaultOrdinaryHoursLimit(opts.baseAwardCode);
}

function sortStrings(values: Iterable<string>): string[] {
  return [...new Set(values)].sort((a, b) => {
    const ai = BAND_ORDER.indexOf(a);
    const bi = BAND_ORDER.indexOf(b);
    const ao = ai === -1 ? BAND_ORDER.length : ai;
    const bo = bi === -1 ? BAND_ORDER.length : bi;
    return ao - bo || a.localeCompare(b);
  });
}

function automatedPenaltyBands(overlay: CompiledEbaOverlay): string[] {
  return sortStrings([...overlay.requiredBands, ...overlay.penalties.map((line) => line.band)]);
}

function automatedAllowanceCodes(overlay: CompiledEbaOverlay): string[] {
  return sortStrings([
    ...overlay.requiredAllowanceCodes,
    ...overlay.allowances.map((line) => line.allowanceCode),
  ]);
}

function automatedConditionalPenaltyCodes(overlay: CompiledEbaOverlay): string[] {
  return sortStrings([
    ...overlay.requiredConditionalPenaltyCodes,
    ...overlay.conditionalPenalties.map((line) => line.blockCode),
  ]);
}

function sampleRate(
  sampleRatesByClassification: Record<string, number>,
): { classificationKey: string; baseHourlyRate: number } | null {
  const entry = Object.entries(sampleRatesByClassification)
    .filter(([, rate]) => Number.isFinite(rate) && rate > 0)
    .sort(([a], [b]) => a.localeCompare(b))[0];
  if (!entry) return null;
  return { classificationKey: entry[0], baseHourlyRate: entry[1] };
}

function dayTypeForBand(band: string): CertDayType | null {
  switch (band) {
    case 'WEEKDAY_DAY':
    case 'WEEKDAY_AFTERNOON':
    case 'WEEKDAY_NIGHT':
    case 'PERMANENT_NIGHT_SHIFT':
      return 'weekday';
    case 'SATURDAY':
      return 'saturday';
    case 'SUNDAY':
      return 'sunday';
    case 'PUBLIC_HOLIDAY':
      return 'publicHoliday';
    default:
      return null;
  }
}

function shiftBandForBand(band: string): CertShiftBand | null {
  switch (band) {
    case 'WEEKDAY_DAY':
      return 'WEEKDAY_DAY';
    case 'WEEKDAY_AFTERNOON':
    case 'WEEKDAY_NIGHT':
    case 'PERMANENT_NIGHT_SHIFT':
      return band;
    default:
      return null;
  }
}

function hasPenaltyLineForEmployment(
  overlay: CompiledEbaOverlay,
  band: string,
  employment: CertEmployment,
): boolean {
  return overlay.penalties.some((line) => line.band === band && line.employmentType === employment);
}

function missingEmploymentReason(overlay: CompiledEbaOverlay, band: string): string | null {
  const missing = EMPLOYMENTS.filter(
    (employment) => !hasPenaltyLineForEmployment(overlay, band, employment),
  );
  return missing.length > 0 ? `MISSING_COMPILED_PENALTY_LINE_FOR_${missing.join('_')}` : null;
}

function structuralReasonForBand(overlay: CompiledEbaOverlay, band: string): string | null {
  if (
    (band === 'WEEKDAY_AFTERNOON' || band === 'WEEKDAY_NIGHT') &&
    overlay.structuralUnsupported.some(
      (block) => block.reason === 'AGED_CARE_COMMENCEMENT_SPAN_REBUILD_REQUIRED',
    )
  ) {
    return 'AGED_CARE_COMMENCEMENT_SPAN_REBUILD_REQUIRED';
  }

  if (
    (band === 'SATURDAY' || band === 'SUNDAY') &&
    overlay.structuralUnsupported.some(
      (block) => block.reason === 'WEEKEND_AS_OVERTIME_REQUIRES_ENGINE_INTERPRETER',
    )
  ) {
    return 'WEEKEND_AS_OVERTIME_REQUIRES_ENGINE_INTERPRETER';
  }

  return null;
}

function lineSupportsEmployment(line: AllowanceLine, employment: CertEmployment): boolean {
  return line.appliesToEmployment === employment || (line.appliesToEmployment ?? null) === null;
}

function defaultAllowanceUnits(line: AllowanceLine): number {
  if (line.basis === 'PER_KM') return 12;
  if (line.basis === 'PER_HOUR') return 2;
  return 1;
}

function scenarioBaseInput(
  opts: GenerateCertMatrixOptions,
  baseHourlyRate: number,
  hoursWorked: number,
  dayType: CertDayType,
  employment: CertEmployment,
  shiftBand?: CertShiftBand | null,
): CertShiftInput {
  return {
    baseAwardCode: opts.baseAwardCode,
    baseHourlyRate,
    hoursWorked,
    dayType,
    employment,
    ordinaryHoursLimit: ordinaryHoursLimitFor(opts),
    ...(shiftBand ? { shiftBand } : {}),
  };
}

function hourFromHHMM(value: string): number | null {
  const [hourRaw = '', minuteRaw = '0'] = value.split(':');
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  return hour + minute / 60;
}

function saturdayGuardDayTypeHours(
  shiftStartTime: string,
  hoursWorked: number,
): NonNullable<CertShiftInput['dayTypeHours']> {
  const startHour = hourFromHHMM(shiftStartTime);
  if (startHour === null) return { saturday: hoursWorked };

  const beforeMidnight = Math.min(hoursWorked, Math.max(0, 24 - startHour));
  const afterMidnight = Math.max(0, hoursWorked - beforeMidnight);

  if (afterMidnight > 0 && afterMidnight >= beforeMidnight) {
    return { weekday: beforeMidnight, saturday: afterMidnight };
  }
  return { saturday: beforeMidnight, sunday: afterMidnight };
}

function weekdayDominantCrossMidnightGuard(
  startWindow: readonly [string, string] | undefined,
  hoursWorked: number,
): { shiftStartTime: string; dayTypeHours: NonNullable<CertShiftInput['dayTypeHours']> } | null {
  if (!startWindow) return null;
  const candidates = [startWindow[0], '16:00', '17:00', '18:00', '19:00', '20:00'];
  for (const candidate of candidates) {
    if (!timeInWindow(candidate, startWindow)) continue;
    const startHour = hourFromHHMM(candidate);
    if (startHour === null) continue;
    const beforeMidnight = Math.min(hoursWorked, Math.max(0, 24 - startHour));
    const afterMidnight = Math.max(0, hoursWorked - beforeMidnight);
    if (beforeMidnight > 0 && afterMidnight > 0 && beforeMidnight > afterMidnight) {
      return {
        shiftStartTime: candidate,
        dayTypeHours: { weekday: beforeMidnight, saturday: afterMidnight },
      };
    }
  }
  return null;
}

function buildBandScenarios(
  overlay: CompiledEbaOverlay,
  opts: GenerateCertMatrixOptions,
  classificationKey: string,
  baseHourlyRate: number,
  ordinaryHours: number,
): {
  scenarios: CertScenario[];
  coveredBands: string[];
  uncoveredBands: Array<{ band: string; reason: string }>;
} {
  const scenarios: CertScenario[] = [];
  const coveredBands = new Set<string>();
  const uncoveredBands: Array<{ band: string; reason: string }> = [];

  for (const band of automatedPenaltyBands(overlay)) {
    const structuralReason = structuralReasonForBand(overlay, band);
    if (structuralReason) {
      uncoveredBands.push({ band, reason: structuralReason });
      continue;
    }

    if (!isEbaCertReducerBandInScope(opts.baseAwardCode, band)) {
      uncoveredBands.push({
        band,
        reason: ebaCertReducerBandUncoveredReason(opts.baseAwardCode, band),
      });
      continue;
    }

    const dayType = dayTypeForBand(band);
    if (!dayType) {
      uncoveredBands.push({
        band,
        reason: ebaCertReducerBandUncoveredReason(opts.baseAwardCode, band),
      });
      continue;
    }

    if (band !== 'WEEKDAY_DAY') {
      const missingReason = missingEmploymentReason(overlay, band);
      if (missingReason) {
        uncoveredBands.push({ band, reason: missingReason });
        continue;
      }
    }

    const shiftBand = shiftBandForBand(band);
    for (const employment of EMPLOYMENTS) {
      scenarios.push({
        id: [
          'l1',
          opts.baseAwardCode.toLowerCase(),
          classificationKey.toLowerCase(),
          band.toLowerCase(),
          employment.toLowerCase(),
        ].join('-'),
        label: `${opts.baseAwardCode} ${classificationKey} ${employment} ${band}`,
        input: scenarioBaseInput(
          opts,
          baseHourlyRate,
          ordinaryHours,
          dayType,
          employment,
          shiftBand,
        ),
        exercisesBands: [band],
        exercisesAllowances: [],
      });
    }
    coveredBands.add(band);
  }

  return { scenarios, coveredBands: sortStrings(coveredBands), uncoveredBands };
}

function buildOvertimeScenarios(
  opts: GenerateCertMatrixOptions,
  classificationKey: string,
  baseHourlyRate: number,
): CertScenario[] {
  const ordinaryLimit = ordinaryHoursLimitFor(opts);
  const crossingHours = opts.overtimeCrossingHours ?? ordinaryLimit + 3;
  return EMPLOYMENTS.map((employment) => ({
    id: [
      'l1',
      opts.baseAwardCode.toLowerCase(),
      classificationKey.toLowerCase(),
      'weekday-overtime-crossing',
      employment.toLowerCase(),
    ].join('-'),
    label: `${opts.baseAwardCode} ${classificationKey} ${employment} weekday overtime crossing`,
    input: scenarioBaseInput(opts, baseHourlyRate, crossingHours, 'weekday', employment),
    exercisesBands: ['OVERTIME_FIRST_2H', 'OVERTIME_AFTER_2H'],
    exercisesAllowances: [],
  }));
}

function conditionalScenarioHours(line: CompiledConditionalPenaltyLine, ordinaryHours: number) {
  if (line.kind === 'MEAL_BREAK')
    return Math.min(ordinaryHours, (line.triggerContinuousHours ?? 5) + 1);
  return ordinaryHours;
}

function hhmmFromMinutes(minutes: number): string {
  const wrapped = ((Math.round(minutes) % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** True when a SHIFT_START_WINDOW line uses the new category-scoped mechanic. */
function isCategoryScopedShiftLoadingLine(line: CompiledConditionalPenaltyLine): boolean {
  return (
    line.kind === 'SHIFT_START_WINDOW' &&
    ((line.employeeCategories?.length ?? 0) > 0 || line.windowMode != null)
  );
}

/**
 * A same-day weekday shift that MATCHES a category-scoped shift-loading line
 * under its windowMode, so the generated cert scenario actually exercises the
 * loading (both oracles then compute it and are compared). Returns null if a
 * clean same-day match can't be constructed (the scenario then degrades to no
 * loading — engine == reducer both $0, still coherent). Provider-agnostic.
 */
function categoryScopedShiftForLine(
  line: CompiledConditionalPenaltyLine,
  hoursWorked: number,
): { employeeCategory: string; shiftStartTime: string; shiftEndTime: string } | null {
  const category = line.employeeCategories?.[0];
  if (!category || !line.startWindow) return null;
  const w0 = hhmmToMinutes(line.startWindow[0]);
  const w1 = hhmmToMinutes(line.startWindow[1]);
  if (w0 === null || w1 === null) return null;
  const dur = Math.round(hoursWorked * 60);
  if (dur <= 0 || dur > 1440) return null;
  const wraps = w1 <= w0; // window crosses midnight (a "night" window)
  const mode = line.windowMode ?? 'COMMENCEMENT';

  let startMin: number;
  if (mode === 'SPAN') {
    startMin = wraps ? 0 : w0; // night → 00:00 (works into the morning part); afternoon → window start
  } else if (mode === 'FINISH') {
    const endTarget = wraps ? Math.min(w0 + 120, 1439) : Math.round((w0 + w1) / 2);
    startMin = endTarget - dur;
  } else {
    startMin = w0; // COMMENCEMENT → start inside the window
  }
  if (startMin < 0) startMin = 0;
  if (startMin + dur > 1440) startMin = 1440 - dur; // keep same-day
  if (startMin < 0 || startMin >= 1440) return null;

  return {
    employeeCategory: category,
    shiftStartTime: hhmmFromMinutes(startMin),
    shiftEndTime: hhmmFromMinutes(startMin + dur),
  };
}

function buildConditionalPenaltyScenarios(
  overlay: CompiledEbaOverlay,
  opts: GenerateCertMatrixOptions,
  classificationKey: string,
  baseHourlyRate: number,
  ordinaryHours: number,
): { scenarios: CertScenario[]; coveredConditionalPenaltyCodes: string[] } {
  const scenarios: CertScenario[] = [];
  const covered = new Set<string>();
  const lines = [...overlay.conditionalPenalties].sort(
    (a, b) =>
      a.kind.localeCompare(b.kind) ||
      a.blockCode.localeCompare(b.blockCode) ||
      a.employmentType.localeCompare(b.employmentType),
  );

  for (const line of lines) {
    const hoursWorked = conditionalScenarioHours(line, ordinaryHours);
    const baseInput = scenarioBaseInput(
      opts,
      baseHourlyRate,
      hoursWorked,
      line.kind === 'PUBLIC_HOLIDAY_NOT_WORKED' ? 'publicHoliday' : 'weekday',
      line.employmentType,
    );
    const id = [
      'l1',
      opts.baseAwardCode.toLowerCase(),
      classificationKey.toLowerCase(),
      'conditional',
      line.blockCode.toLowerCase(),
      line.employmentType.toLowerCase(),
    ].join('-');
    const label = `${opts.baseAwardCode} ${classificationKey} ${line.employmentType} ${line.blockCode}`;

    const categoryScoped = isCategoryScopedShiftLoadingLine(line);
    const categoryScopedMatch = categoryScoped
      ? categoryScopedShiftForLine(line, hoursWorked)
      : null;

    let input: CertShiftInput;
    switch (line.kind) {
      case 'SHIFT_START_WINDOW':
        input = categoryScoped
          ? categoryScopedMatch
            ? {
                ...baseInput,
                employeeCategory: categoryScopedMatch.employeeCategory,
                shiftStartTime: categoryScopedMatch.shiftStartTime,
                shiftEndTime: categoryScopedMatch.shiftEndTime,
                dayTypeHours: { weekday: hoursWorked },
              }
            : {
                ...baseInput,
                employeeCategory: line.employeeCategories?.[0] ?? null,
                shiftStartTime: line.startWindow?.[0] ?? '15:00',
              }
          : {
              ...baseInput,
              conditionalPenaltyCode: line.blockCode,
              shiftStartTime: line.startWindow?.[0] ?? '15:00',
            };
        break;
      case 'MEAL_BREAK':
        input = {
          ...baseInput,
          conditionalPenaltyCode: line.blockCode,
          missedMealBreak: true,
          continuousHoursWithoutMealBreak: hoursWorked,
        };
        break;
      case 'INTERRUPTED_MEAL_BREAK':
        input = {
          ...baseInput,
          conditionalPenaltyCode: line.blockCode,
          interruptedMealBreak: true,
          interruptedMealBreakMinutes: 30,
        };
        break;
      case 'PUBLIC_HOLIDAY_NOT_WORKED':
        input = {
          ...baseInput,
          conditionalPenaltyCode: line.blockCode,
          publicHolidayNotWorked: true,
          usualRosteredHours: ordinaryHours,
        };
        break;
    }

    scenarios.push({
      id,
      label,
      input,
      exercisesBands: [],
      exercisesAllowances: [],
      exercisesConditionalPenalties: [line.blockCode],
    });
    if (line.kind === 'SHIFT_START_WINDOW' && categoryScoped) {
      // Saturday no-stack guard for the category-scoped mechanic: a Saturday
      // shift for this category attracts the Saturday penalty and NO shift
      // loading (the new path only fires on a dominant weekday) — verifies the
      // substitution rule in both oracles.
      const guardShiftStartTime =
        categoryScopedMatch?.shiftStartTime ?? line.startWindow?.[0] ?? '15:00';
      scenarios.push({
        id: `${id}-saturday-guard`,
        label: `${label} Saturday no-stack guard`,
        input: {
          ...baseInput,
          dayType: 'saturday',
          employeeCategory:
            categoryScopedMatch?.employeeCategory ?? line.employeeCategories?.[0] ?? null,
          shiftStartTime: guardShiftStartTime,
          dayTypeHours: saturdayGuardDayTypeHours(guardShiftStartTime, hoursWorked),
        },
        exercisesBands: ['SATURDAY'],
        exercisesAllowances: [],
        exercisesConditionalPenalties: [line.blockCode],
      });
    } else if (line.kind === 'SHIFT_START_WINDOW') {
      const guardShiftStartTime = line.startWindow?.[0] ?? '15:00';
      const weekdayCrossMidnightGuard = weekdayDominantCrossMidnightGuard(
        line.startWindow,
        hoursWorked,
      );
      if (weekdayCrossMidnightGuard) {
        scenarios.push({
          id: `${id}-weekday-cross-midnight-segment-guard`,
          label: `${label} weekday cross-midnight segment guard`,
          input: {
            ...baseInput,
            conditionalPenaltyCode: line.blockCode,
            shiftStartTime: weekdayCrossMidnightGuard.shiftStartTime,
            dayTypeHours: weekdayCrossMidnightGuard.dayTypeHours,
          },
          exercisesBands: ['WEEKDAY_DAY', 'SATURDAY'],
          exercisesAllowances: [],
          exercisesConditionalPenalties: [line.blockCode],
        });
      }
      scenarios.push({
        id: `${id}-saturday-guard`,
        label: `${label} Saturday no-stack guard`,
        input: {
          ...baseInput,
          dayType: 'saturday',
          conditionalPenaltyCode: line.blockCode,
          shiftStartTime: guardShiftStartTime,
          dayTypeHours: saturdayGuardDayTypeHours(guardShiftStartTime, hoursWorked),
        },
        exercisesBands: ['SATURDAY'],
        exercisesAllowances: [],
        exercisesConditionalPenalties: [line.blockCode],
      });
    }
    // A category-scoped line is only "covered" if we actually built a matching
    // shift; if not, both oracles would pay $0 and vacuously agree, so leave it
    // uncovered rather than pretend the loading was exercised.
    if (!categoryScoped || categoryScopedMatch) {
      covered.add(line.blockCode);
    }
  }

  return { scenarios, coveredConditionalPenaltyCodes: sortStrings(covered) };
}

function buildAllowanceScenarios(
  overlay: CompiledEbaOverlay,
  opts: GenerateCertMatrixOptions,
  classificationKey: string,
  baseHourlyRate: number,
  ordinaryHours: number,
): { scenarios: CertScenario[]; coveredAllowances: string[] } {
  const scenarios: CertScenario[] = [];
  const coveredAllowances = new Set<string>();
  const allowanceCodes = sortStrings(overlay.allowances.map((line) => line.allowanceCode));

  for (const code of allowanceCodes) {
    for (const employment of EMPLOYMENTS) {
      const line = overlay.allowances.find(
        (candidate) =>
          candidate.allowanceCode === code && lineSupportsEmployment(candidate, employment),
      );
      if (!line) continue;
      const units = opts.allowanceUnitsByCode?.[code] ?? defaultAllowanceUnits(line);
      scenarios.push({
        id: [
          'l1',
          opts.baseAwardCode.toLowerCase(),
          classificationKey.toLowerCase(),
          'allowance',
          code.toLowerCase(),
          employment.toLowerCase(),
        ].join('-'),
        label: `${opts.baseAwardCode} ${classificationKey} ${employment} ${code} allowance`,
        input: {
          ...scenarioBaseInput(opts, baseHourlyRate, ordinaryHours, 'weekday', employment),
          allowanceUnits: { [code]: units },
        },
        exercisesBands: ['WEEKDAY_DAY'],
        exercisesAllowances: [code],
      });
      coveredAllowances.add(code);
    }
  }

  return { scenarios, coveredAllowances: sortStrings(coveredAllowances) };
}

function uncoveredAllowances(
  overlay: CompiledEbaOverlay,
  coveredAllowances: readonly string[],
): Array<{ allowanceCode: string; reason: string }> {
  const covered = new Set(coveredAllowances);
  return automatedAllowanceCodes(overlay)
    .filter((code) => !covered.has(code))
    .map((allowanceCode) => ({
      allowanceCode,
      reason: overlay.manualContracts.some((manual) =>
        manual.requiredAllowanceCodes.includes(allowanceCode),
      )
        ? 'MANUAL_OR_DEFERRED_ALLOWANCE_CONTRACT'
        : 'ALLOWANCE_LINE_NOT_COMPILED',
    }));
}

export function generateCertMatrix(
  overlay: CompiledEbaOverlay,
  opts: GenerateCertMatrixOptions,
): { scenarios: CertScenario[]; coverage: CertCoverageReport } {
  const sample = sampleRate(opts.sampleRatesByClassification);
  const automatedBands = automatedPenaltyBands(overlay);
  const automatedAllowances = automatedAllowanceCodes(overlay);
  const automatedConditionals = automatedConditionalPenaltyCodes(overlay);

  if (!sample) {
    return {
      scenarios: [],
      coverage: {
        automatedBands,
        coveredBands: [],
        uncoveredBands: automatedBands.map((band) => ({
          band,
          reason: 'NO_SAMPLE_RATE_PROVIDED',
        })),
        manualContracts: overlay.manualContracts,
        structuralUnsupported: overlay.structuralUnsupported,
        automatedAllowances,
        coveredAllowances: [],
        uncoveredAllowances: automatedAllowances.map((allowanceCode) => ({
          allowanceCode,
          reason: 'NO_SAMPLE_RATE_PROVIDED',
        })),
        automatedConditionalPenaltyCodes: automatedConditionals,
        coveredConditionalPenaltyCodes: [],
        uncoveredConditionalPenalties: automatedConditionals.map((blockCode) => ({
          blockCode,
          reason: 'NO_SAMPLE_RATE_PROVIDED',
        })),
      },
    };
  }

  const ordinaryHours = opts.ordinaryHoursWorked ?? ordinaryHoursLimitFor(opts);
  const bandMatrix = buildBandScenarios(
    overlay,
    opts,
    sample.classificationKey,
    sample.baseHourlyRate,
    ordinaryHours,
  );
  const overtimeScenarios = buildOvertimeScenarios(
    opts,
    sample.classificationKey,
    sample.baseHourlyRate,
  );
  const allowanceMatrix = buildAllowanceScenarios(
    overlay,
    opts,
    sample.classificationKey,
    sample.baseHourlyRate,
    ordinaryHours,
  );
  const conditionalMatrix = buildConditionalPenaltyScenarios(
    overlay,
    opts,
    sample.classificationKey,
    sample.baseHourlyRate,
    ordinaryHours,
  );

  const loadedOvertimeScenarios = hasGeneralStaffDailyLoadedOvertime(overlay)
    ? conditionalMatrix.scenarios
        .filter(
          (scenario) =>
            scenario.input.dayType === 'weekday' &&
            scenario.input.hoursWorked > 3 &&
            !scenario.input.dayTypeHours?.saturday &&
            overlay.conditionalPenalties.some(
              (line) =>
                line.kind === 'SHIFT_START_WINDOW' &&
                scenario.exercisesConditionalPenalties?.includes(line.blockCode),
            ),
        )
        .map(
          (scenario): CertScenario => ({
            ...scenario,
            id: `${scenario.id}-loaded-overtime`,
            label: `${scenario.label} loaded weekday overtime`,
            input: {
              ...scenario.input,
              ordinaryHoursLimit: scenario.input.hoursWorked - 3,
              ...(scenario.input.dayTypeHours
                ? { dayTypeHours: { weekday: scenario.input.hoursWorked - 3 } }
                : {}),
            },
            exercisesBands: ['OVERTIME_FIRST_2H', 'OVERTIME_AFTER_2H'],
          }),
        )
    : [];

  const coveredBands = sortStrings(bandMatrix.coveredBands);
  const accountedBands = new Set([
    ...coveredBands,
    ...bandMatrix.uncoveredBands.map((entry) => entry.band),
  ]);
  const silentGaps = automatedBands.filter((band) => !accountedBands.has(band));
  const uncoveredBands = [
    ...bandMatrix.uncoveredBands,
    ...silentGaps.map((band) => ({ band, reason: 'MATRIX_GENERATOR_SILENT_GAP_GUARD' })),
  ];

  const coveredConditionals = new Set(conditionalMatrix.coveredConditionalPenaltyCodes);
  const uncoveredConditionalPenalties = automatedConditionals
    .filter((blockCode) => !coveredConditionals.has(blockCode))
    .map((blockCode) => ({
      blockCode,
      reason: 'CONDITIONAL_PENALTY_LINE_NOT_COMPILED',
    }));

  return {
    scenarios: [
      ...bandMatrix.scenarios,
      ...overtimeScenarios,
      ...loadedOvertimeScenarios,
      ...allowanceMatrix.scenarios,
      ...conditionalMatrix.scenarios,
    ].map((scenario) =>
      hasGeneralStaffDailyLoadedOvertime(overlay) && opts.baseAwardCode === 'MA000018'
        ? {
            ...scenario,
            input: {
              ...scenario.input,
              employeeCategory:
                scenario.input.employeeCategory ??
                (scenario.input.shiftStartTime ||
                (scenario.input.shiftBand && scenario.input.shiftBand !== 'WEEKDAY_DAY')
                  ? 'SHIFTWORKER'
                  : 'DAYWORKER'),
            },
          }
        : scenario,
    ),
    coverage: {
      automatedBands,
      coveredBands,
      uncoveredBands,
      manualContracts: overlay.manualContracts,
      structuralUnsupported: overlay.structuralUnsupported,
      automatedAllowances,
      coveredAllowances: allowanceMatrix.coveredAllowances,
      uncoveredAllowances: uncoveredAllowances(overlay, allowanceMatrix.coveredAllowances),
      automatedConditionalPenaltyCodes: automatedConditionals,
      coveredConditionalPenaltyCodes: conditionalMatrix.coveredConditionalPenaltyCodes,
      uncoveredConditionalPenalties,
    },
  };
}
